import styled from '@emotion/styled';
import tw from 'twin.macro';
import dayjs from 'dayjs';
import { Popover } from 'antd';
import Calender, { DisabledDatesType } from 'components/molecules/Calender';
import { default as CalenderIcon } from 'assets/svg/Calender';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { FilterContextData } from 'contexts/FilterContext';
import { calculateFontSize, getConfig } from 'utils/helperMethods';
import { colorConstants } from 'config/colorConstants';
import { TooltipPlacement } from 'antd/es/tooltip';
import { UserContext } from 'contexts/ConfigContext';
import MonthlyCalenderRange from 'components/molecules/MonthlyCalenderRange';
import QuarterCalenderRange from 'components/molecules/QuarterCalenderRange';

export type DateRangeType = [Date | null, Date | null];

interface Props {
    setStartDate: any;
    setEndDate: any;
    startDate?: string;
    endDate?: string;
    isDisabled?: boolean;
    calenderHeader?: string;
    position?: TooltipPlacement;
}

const scaledWidth = calculateFontSize(window.innerWidth, 13);

const CalenderHeaderDiv = styled.div`
    ${tw`pb-1`};
    font-size: ${scaledWidth}px;
    color: ${colorConstants.fontGrey};
    font-weight: 500;
`;

const CalenderPriceChange: FC<Props> = ({
    setStartDate,
    setEndDate,
    isDisabled,
    startDate,
    endDate,
    calenderHeader,
    position = 'right',
}) => {
    const { latestDate, calenderStartDay, currentFrequency } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const isMonthlyGranularity = currentFrequency?.key?.toLowerCase() === 'm';
    const isQuarterGranularity = currentFrequency?.key?.toLowerCase() === 'q';
    const formattedLatestData = dayjs(latestDate, commonConfig?.dateFormat).format('MM/DD/YYYY');
    const latestYear = dayjs(latestDate, commonConfig?.dateFormat).year();
    const [isOpen, setIsOpen] = useState(false);

    const [tempRange, setTempRange] = useState<any>([
        startDate
            ? isMonthlyGranularity || isQuarterGranularity
                ? dayjs(startDate, commonConfig?.dateFormat)
                : new Date(dayjs(startDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
            : null,
        endDate
            ? isMonthlyGranularity || isQuarterGranularity
                ? dayjs(endDate, commonConfig?.dateFormat)
                : new Date(dayjs(endDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
            : null,
    ]);
    const [stateForCalender, setStateForCalender] = useState({ step: 1, dateRange: [null, null] });
    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        let endLabel = 'End Date';
        const { dateRange } = stateForCalender;
        if (dateRange[0]) {
            startLabel =
                isMonthlyGranularity || isQuarterGranularity
                    ? dateRange[0]
                    : dayjs(new Date(dateRange[0])).format(commonConfig?.dateFormat);
            setStartDate(startLabel);
        }
        if (dateRange[1]) {
            endLabel =
                isMonthlyGranularity || isQuarterGranularity
                    ? dateRange[1]
                    : dayjs(new Date(dateRange[1])).format(commonConfig?.dateFormat);
            setEndDate(endLabel);
        }
        return { startLabel, endLabel };
    }, [stateForCalender]);

    const handleDateChange = (range: DateRangeType) => {
        setTempRange([...range]);
    };
    const handleOpenChange = (
        newOpen: boolean,
        isClickOutside: boolean = true,
        from: 'header' | 'dateRange' = 'dateRange',
    ) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            setStateForCalender((prev: any) => ({ ...prev, dateRange: tempRange }));
            // const startDateUnix = dayjs(tempRange[0]).add(utcOffset, 'm').subtract(6, 'day').unix();
            // const endDateUnix = dayjs(tempRange[1]).add(utcOffset, 'm').subtract(6, 'day').unix();
        } else if (!newOpen)
            setTempRange([
                startDate
                    ? new Date(dayjs(startDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
                    : stateForCalender.dateRange?.[0],
                endDate
                    ? new Date(dayjs(endDate, commonConfig?.dateFormat).format('MM/DD/YYYY'))
                    : stateForCalender.dateRange?.[1],
            ]); //if clicked outside dont save the change
    };
    //called when clicked on set date for monthly calender
    const onMonthlyQuarterlySubmit = (startDateUnix: number, endDateUnix: number) => {
        //only set calender state when clicked on set date
        setStateForCalender((prev: any) => ({ ...prev, dateRange: tempRange }));
    };
    const disabledDates: DisabledDatesType = [null, formattedLatestData];
    const calenderDatesConfig = {
        startYear: latestYear === 1970 || !latestYear ? undefined : latestYear,
        disabledDates,
    };

    return (
        <div>
            <CalenderHeaderDiv>{calenderHeader}</CalenderHeaderDiv>
            {isMonthlyGranularity ? (
                <MonthlyCalenderRange
                    disabled={isDisabled}
                    tempRange={tempRange}
                    setTempRange={setTempRange}
                    onDateSubmit={onMonthlyQuarterlySubmit}
                    calenderDatesConfig={calenderDatesConfig}
                />
            ) : isQuarterGranularity ? (
                <QuarterCalenderRange
                    tempRange={tempRange}
                    setTempRange={setTempRange}
                    onDateSubmit={onMonthlyQuarterlySubmit}
                    calenderDatesConfig={calenderDatesConfig}
                />
            ) : (
                <Popover
                    trigger="click"
                    open={isOpen && !isDisabled}
                    onOpenChange={(open) => handleOpenChange(open)}
                    placement={position}
                    content={
                        <Calender
                            startYear={latestYear === 1970 || !latestYear ? undefined : latestYear}
                            disabledDates={[null, latestDate]}
                            startDay={calenderStartDay}
                            range={tempRange}
                            onChange={handleDateChange}
                            onFooterClick={() => handleOpenChange(false, false)}
                        />
                    }
                >
                    <div className="flex justify-center items-center w-full">
                        <div
                            className="cursor-pointer w-full flex justify-start gap-1 py-1 items-center px-1 border border-purpleFont rounded"
                            style={isDisabled ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                        >
                            <CalenderIcon />
                            <div className="flex w-full justify-center">
                                <span className="text-subHeaderColor pt-[1px] font-xs">
                                    {startDate || dateLabel.startLabel}
                                </span>
                                <span className="text-subHeaderColor mx-1"> - </span>
                                <span className="text-subHeaderColor pt-[1px] font-xs">
                                    {endDate || dateLabel.endLabel}
                                </span>
                            </div>
                        </div>
                    </div>
                </Popover>
            )}
        </div>
    );
};
export default CalenderPriceChange;
